/*
  Admin.js - Admin Provider Main Component
*/

// NPM MODULES
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Stack,
  InputLabel,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  FormGroup,
  Box,
  FormControlLabel,
} from '@mui/material';

// COMPONENTS
import CountrySelect from '../../common/inputs/CountrySelect';
import LanguagePicker from '../../common/LanguagePicker';
import Loader from '../../common/Loader';

// FORMATTED MESSAGES
import ProvidersFormattedMessages from '../../../messages/admin/ProvidersFormattedMessages';

// NETWORKING
import useAdminRequest from '../../../requests/AdminRequest';
import useProvidersRequest from '../../../requests/ProviderRequest';

const ProviderSingleView = ({
  provider,
  setPageTitle,
  errorHandler,
  updateActingProviderDetails,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [singleProvider, setSingleProvider] = useState(null);
  const [tariffPlans, setTariffPlans] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const { getProviderExtra, updateProvider } = useAdminRequest();
  const { getSingleProvider, getTariffPlans } = useProvidersRequest();

  useEffect(() => {
    setPageTitle(ProvidersFormattedMessages.providerInformation);
    const fetchData = async () => {
      try {
        // Use this Provider instead of the props one as this has more data
        const fetchedProvider = await getSingleProvider(
          provider.provider_id,
          errorHandler
        );

        // Get additional data
        const fetchedTariffPlans = await getTariffPlans(
          provider.provider_id,
          errorHandler
        );
        const extras = await getProviderExtra(errorHandler);

        // Update the acting_provider_id cookie with the selected Provider
        updateActingProviderDetails(provider.provider_id);

        setSingleProvider(fetchedProvider);
        setTariffPlans(fetchedTariffPlans);
        setStatuses(extras.provider_statuses);
        setXeroAccounts(extras.xero_accounts);
      } catch (e) {
        errorHandler(e.message);
      }
    };

    fetchData();
  }, []);

  /*
    Handles Edit Mode on/off
  */
  const toggleEdit = () => {
    setIsEditing((prevState) => !prevState);
  };

  /*
    Handles changes in the form
  */
  const handleFormChange = (name, value) => {
    setEdits((prevEdits) => ({
      ...prevEdits,
      [name]: value,
    }));
  };

  /*
    Handles Edit form submission
  */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateProvider(provider.provider_id, edits, errorHandler);
      window.location.href = '/admin/provider'; // Force reload - TODO: redo properly
    } catch (e) {
      // Handle error
    }
  };

  /*
    Generates the Tariff Plan Select control.
  */
  const generateTariffPlanSelect = () => {
    const propProvider = provider;
    let providerToUse = singleProvider || propProvider;

    if (!providerToUse) return null;

    // If no tariff plans, return nothing
    if (!tariffPlans || tariffPlans.length === 0) return null;

    return (
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          alignItems: { md: 'center' },
          gap: { md: 2 },
        }}
      >
        <InputLabel sx={{ minWidth: 200 }}>Tariff Plan</InputLabel>
        <TextField
          select
          name="tariff_plan_id"
          value={edits.tariff_plan_id || providerToUse.tariff_plan_id || ''}
          onChange={(e) => handleFormChange('tariff_plan_id', e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
        >
          {tariffPlans.map((tariffPlan) => (
            <MenuItem
              key={tariffPlan.tariff_plan_id}
              value={tariffPlan.tariff_plan_id}
            >
              {tariffPlan.tariff_plan_name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    );
  };

  /*
    Generates the Select control for Provider Statuses
  */
  const generateStatusSelect = () => {
    const propProvider = provider;
    let providerToUse = singleProvider || propProvider;

    let statusOptions = statuses;

    // If no data from API, set default list
    if (!statusOptions || statusOptions.length === 0) {
      statusOptions = [
        { meaning: 'Trial', code: 'T' },
        { meaning: 'Active', code: 'A' },
        { meaning: 'Cancelled', code: 'C' },
        { meaning: 'Suspended', code: 'S' },
      ];
    }

    return (
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          alignItems: { md: 'center' },
          gap: { md: 2 },
        }}
      >
        <InputLabel sx={{ minWidth: 200 }}>
          {ProvidersFormattedMessages.statusText}
        </InputLabel>
        <TextField
          select
          name="provider_status"
          value={edits.provider_status || providerToUse.provider_status || ''}
          onChange={(e) => handleFormChange('provider_status', e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
        >
          {statusOptions.map((status, index) => (
            <MenuItem key={index} value={status.code}>
              {status.meaning}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    );
  };

  /*
    Generates the Select control for Provider Xero Accounts
  */
  const generateXeroSelect = () => {
    const propProvider = provider;
    let providerToUse = singleProvider || propProvider;

    return (
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          alignItems: { md: 'center' },
          gap: { md: 2 },
        }}
      >
        <InputLabel sx={{ minWidth: 200 }}>Xero Account</InputLabel>
        {xeroAccounts && (
          <TextField
            select
            name="xero_accounts_id"
            value={
              edits.xero_accounts_id || providerToUse.xero_accounts_id || ''
            }
            onChange={(e) =>
              handleFormChange('xero_accounts_id', e.target.value)
            }
            variant="outlined"
            size="small"
            fullWidth
          >
            {xeroAccounts.map((xeroAccount) => (
              <MenuItem
                key={xeroAccount.xero_accounts_id}
                value={xeroAccount.xero_accounts_id}
              >
                {xeroAccount.account_name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
    );
  };

  /*
    Convenience function to generate Form Groups
  */
  const generateFormGroup = (label, displayValue) => {
    return (
      <Grid size={12}>
        <Typography>
          <strong>{label}</strong> {displayValue}
        </Typography>
      </Grid>
    );
  };

  const generateBody = () => {
    const propProvider = provider;
    const providerToUse = singleProvider || propProvider;

    if (!providerToUse) return <Typography>No Data</Typography>;

    if (isEditing) {
      return (
        <Box
          component="form"
          onChange={(e) => handleFormChange(e.target.name, e.target.value)}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  {ProvidersFormattedMessages.nameText}
                </InputLabel>
                <TextField
                  name="provider_name"
                  defaultValue={providerToUse.provider_name}
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>Copy From:</InputLabel>
                <TextField
                  name="copy_dlvry_from"
                  defaultValue={providerToUse.copy_dlvry_from}
                  variant="outlined"
                  size="small"
                  helperText="(Root Provider: 16f5fd20-8911-11e9-8735-6772c8e323ae)"
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>{generateStatusSelect()}</Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>Country</InputLabel>
                <CountrySelect
                  name="country_id"
                  fullWidth
                  selectDefault
                  value={edits.country_id || providerToUse.country_id}
                  onChange={(_, newValue) =>
                    handleFormChange('country_id', newValue)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>{generateTariffPlanSelect()}</Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>Language</InputLabel>
                <LanguagePicker
                  optionsName="language_id"
                  name="language_id"
                  selectedLanguageId={
                    edits.language_id || providerToUse.language_id
                  }
                  onChange={(value) => {
                    setEdits((prev) => ({
                      ...prev,
                      language_id: value,
                    }));
                  }}
                  errorHandler={errorHandler}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        type="checkbox"
                        defaultChecked={providerToUse.active_creation === 'Y'}
                        name="active_creation"
                      />
                    }
                    label="Active Creation"
                  />
                </FormGroup>
              </Stack>
            </Grid>
            <Grid size={12}>{generateXeroSelect()}</Grid>
          </Grid>
        </Box>
      );
    }

    if (!singleProvider) return <Loader />;

    return (
      <Grid container spacing={2}>
        {generateFormGroup('Name:', providerToUse.provider_name)}
        {generateFormGroup('Copy From:', providerToUse.copy_dlvry_from)}
        {generateFormGroup('Status:', providerToUse.provider_status_name)}
        {generateFormGroup('Country:', providerToUse.country_name)}
        {generateFormGroup('Language:', providerToUse.language_name)}
        {generateFormGroup('Tariff Plan:', providerToUse.tariff_plan_name)}
        {generateFormGroup(
          'Active Creation:',
          providerToUse.active_creation === 'Y' ? 'Yes' : 'No'
        )}
        {generateFormGroup('Xero Account:', providerToUse.xero_accounts_name)}
      </Grid>
    );
  };

  const generateButtons = () => {
    const propProvider = provider;
    const providerToUse = singleProvider || propProvider;

    if (!providerToUse) return null;

    let buttonText = isEditing ? 'Cancel' : 'Edit';

    // Show single Edit button if not editing
    if (providerToUse && !isEditing)
      return (
        <Box>
          <Button variant="contained" color="secondary" onClick={toggleEdit}>
            {buttonText}
          </Button>
        </Box>
      );

    return (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end' }}>
        <Button variant="outlined" color="secondary" onClick={toggleEdit}>
          {buttonText}
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSubmit}>
          Save
        </Button>
      </Box>
    );
  };

  return (
    <Stack gap={4} sx={{ maxWidth: 800 }}>
      {generateBody()}
      {generateButtons()}
    </Stack>
  );
};

export default ProviderSingleView;
